import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import useStudioDocument from "./useStudioDocument";
import useStudioGestures from "./useStudioGestures";
import useStudioShortcuts from "./useStudioShortcuts";
import { cmsTextChange, replaceCmsSource } from "./studioCmsText";
import { unpackArtwork } from "./studioAssets";
import { fitDescriptionFrames, fitPriceFrames as fitPrices } from "./studioTextFit";
import { fitLinkedText } from "./studioLinkedText";
import { alignHeadingWinglets } from "./studioHeadings";
import StudioInlineText from "./StudioInlineText";
import { recoverArtworkTransforms, replacementArtworkMatrix, transformArtwork, replaceArtwork } from "./studioArtwork";
import StudioCanvas, { NodeArtwork } from "./StudioCanvas";
import StudioMenuPreview from "./StudioMenuPreview";
import StudioPageName from "./StudioPageName";
import StudioLibrary from "./StudioLibrary";
import { groupPath, withGroupPath, createGroup, ungroupSelection, moveLayers, reorderLayers, portableSelection, canvasGroupSelection, canvasShiftSelection } from "./studioGroups";
import { transferToPage } from "./studioPageTransfer";
import { flowChildFor, gapHandles, pinChild } from "./studioFlow";
import { detachContent } from "./studioContentLinks";
import StudioInspector from "./StudioInspector";
import {
  deleteStyleGroup,
  styleGroupIds,
  styleGroupName,
  updateStyleGroup,
  toggleStyleGroupMember,
  applyStyleGroup,
} from "./studioStyleGroups";
import { Button, Icon } from "./StudioControls";
import { ExportDialog, Modal, ShortcutsDialog } from "./StudioDialog";
import { preflight } from "./studioExport";
import {
  alignElements,
  applyTypography,
  clone,
  contentIndex,
  distributeElements,
  element,
  generateDocument,
  patchElements,
  matchingIds,
  uid,
  validateDocument,
} from "./studioModel";
import "./MenuStudio.css";

// The menu page is the focal point; compact linen-toned tools serve the printed artifact.
// Brass signals selection/linkage. Georgia gives the menu character; system sans keeps controls quiet.
// 4pt spacing grid, 12/16px panel rhythm, thin warm boundaries, restrained paper elevation.
export default function MenuStudio({
  menus,
  cmsView,
  onCmsUpdate,
  initialMenu = "dinner",
  initialView = "design",
  onContextChange,
  initialDocuments,
  initialExport = false,
  importOriginal,
  onClose,
  bridgeRef,
}) {
  const [menuKey, setMenuKey] = useState(initialMenu);
  const [view, setView] = useState(initialView);
  return (
    <StudioSession
      key={menuKey}
      menus={menus}
      cmsView={cmsView}
      view={view}
      setView={setView}
      onCmsUpdate={onCmsUpdate}
      initialDocuments={initialDocuments}
      initialExport={initialExport}
      importOriginal={importOriginal}
      menuKey={menuKey}
      setMenuKey={setMenuKey}
      onContextChange={onContextChange}
      onClose={onClose}
      bridgeRef={bridgeRef}
    />
  );
}
const fitPriceFrames = (doc, content) => fitLinkedText(fitPrices(doc, content), content);

function StudioSession({
  menus,
  cmsView,
  view,
  setView,
  onCmsUpdate,
  menuKey,
  initialDocuments,
  initialExport,
  importOriginal,
  setMenuKey,
  onContextChange,
  onClose,
  bridgeRef,
}) {
  const [liveMenus, setLiveMenus] = useState(menus);
  const liveMenusRef = useRef(menus);
  const textSaveQueue = useRef(Promise.resolve());
  useEffect(() => { liveMenusRef.current = menus; setLiveMenus(menus); }, [menus]);
  const {
    doc,
    current,
    commit,
    commitWithCms,
    commitAuthored,
    getRevision,
    authored,
    repairs,
    applyRepair,
    unresolved,
    recoveryState,
    onCmsResults,
    preview: previewDoc,
    undo: undoDocument,
    redo: redoDocument,
    historyBusy,
    canUndo,
    canRedo,
    save,
    useCloud,
    syncFromCloud,
    status,
    error,
    setError,
  } = useStudioDocument(liveMenus, menuKey, initialDocuments);
  const [pageId, setActivePageId] = useState(null),
    [selection, setSelection] = useState([]),
    [focusedLayerId, setFocusedLayerId] = useState(null),
    [activeStyleGroup, setActiveStyleGroup] = useState(null),
    [tab, setTab] = useState("pages"),
    [scope, setScope] = useState("selection");
  useEffect(() => {
    if (doc && activeStyleGroup?.startsWith("custom:") && !doc.styleGroups?.[activeStyleGroup]) setActiveStyleGroup(null);
  }, [doc, activeStyleGroup]);
  const selected = useMemo(() => {
    if (!doc) return selection;
    if (activeStyleGroup) return styleGroupIds(doc, activeStyleGroup);
    if (scope === "selection") return selection;
    const anchorPage = doc.pages.find((p) => p.elements.some((n) => n.id === selection[0]));
    const anchor = anchorPage?.elements.find((n) => n.id === selection[0]);
    return anchor ? matchingIds(doc, anchor, scope, anchorPage.id, selection) : selection;
  }, [doc, activeStyleGroup, selection, scope]);
  const setSelected = useCallback((ids, focusId = null) => {
    setFocusedLayerId(focusId);
    setActiveStyleGroup(null);
    setScope("selection");
    setSelection(ids);
  }, []);
  const [fitRequest, setFitRequest] = useState(0);
  const [zoom, setZoomValue] = useState(0.8),
    [tool, setTool] = useState("select"),
    [grid, setGrid] = useState(false),
    [margins, setMargins] = useState(true),
    [snap, setSnap] = useState(true);
  const [preview, setPreview] = useState(false),
    [guides, setGuides] = useState([]),
    [dragVisual, setDragVisual] = useState(null),
    [marquee, setMarquee] = useState(null),
    [dialog, setDialog] = useState(initialExport ? "export" : null),
    [editing, setEditing] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(
      () => window.innerWidth > 700
    ),
    [libraryOpen, setLibraryOpen] = useState(() => window.innerWidth > 700),
    [toast, setToast] = useState("");
  const [textEditors, setTextEditors] = useState([]);
  const [headingDrafts, setHeadingDrafts] = useState({});
  const inlineRefs = useRef(new Map());
  const saveInlineEdits = useCallback(async () => {
    const results = await Promise.all([...inlineRefs.current.values()].map((editor) => editor.save()));
    return results.every(Boolean);
  }, []);
  // The surrounding CMS asks the open design to save before a deletion and to
  // take on the server's cleanup (linked elements removed) afterwards.
  useEffect(() => {
    if (!bridgeRef) return undefined;
    bridgeRef.current = {
      beforeCmsDelete: async () => { await saveInlineEdits(); await save(); },
      afterCmsDelete: () => syncFromCloud(),
    };
    return () => { bridgeRef.current = null; };
  }, [bridgeRef, saveInlineEdits, save, syncFromCloud]);
  const closeTextEditor = (id) => {
    setHeadingDrafts((previous) => { const next = { ...previous }; delete next[id]; return next; });
    setTextEditors((previous) => previous.filter((node) => node.id !== id));
    setEditing((previous) => previous?.id === id ? null : previous);
  };
  const openExport = async () => {
    if (!(await saveInlineEdits())) return;
    setEditing(null);
    setDialog("export");
  };
  const replaceArtworkRef = useRef(null);
  const replacementIds = useRef([]);
  const [replacingArtwork, setReplacingArtwork] = useState(false);
  const historyAction = useCallback((action) => {
    if (!inlineRefs.current.size) return action();
    return saveInlineEdits().then((ok) => { if (ok) { setEditing(null); setTextEditors([]); setHeadingDrafts({}); action(); } });
  }, [saveInlineEdits]);
  const undo = useCallback(() => historyAction(undoDocument), [historyAction, undoDocument]);
  const redo = useCallback(() => historyAction(redoDocument), [historyAction, redoDocument]);
  const dialogRef = useRef(null),
    workspaceRef = useRef(null),
    svgRef = useRef(null),
    pageRefs = useRef(new Map()),
    pendingPage = useRef(undefined),
    uploadRef = useRef(null),
    importRef = useRef(null),
    gesture = useRef(null),
    clipboard = useRef([]),
    space = useRef(false),
    zoomFitDone = useRef(false),
    zoomAnchor = useRef(null),
    fitPage = useRef(null),
    renderedZoom = useRef(zoom);
  const setZoom = useCallback((next, pointer) => {
    const workspace = workspaceRef.current;
    if (!workspace || gesture.current) return;
    const viewport = workspace.getBoundingClientRect();
    const clientX =
      pointer?.clientX ?? viewport.left + workspace.clientWidth / 2;
    const clientY =
      pointer?.clientY ?? viewport.top + workspace.clientHeight / 2;
    let nearest = null,
      distance = Infinity;
    pageRefs.current.forEach((area) => {
      const paper = area.querySelector("svg.ms-paper");
      if (!paper) return;
      const rect = paper.getBoundingClientRect();
      const dx = Math.max(rect.left - clientX, clientX - rect.right, 0);
      const dy = Math.max(rect.top - clientY, clientY - rect.bottom, 0);
      if (dx * dx + dy * dy < distance) {
        distance = dx * dx + dy * dy;
        nearest = { paper, rect };
      }
    });
    if (nearest) {
      zoomAnchor.current = {
        paper: nearest.paper,
        x: (clientX - nearest.rect.left) / renderedZoom.current,
        y: (clientY - nearest.rect.top) / renderedZoom.current,
        clientX,
        clientY,
      };
    }
    setZoomValue(next);
  }, []);
  useLayoutEffect(() => {
    renderedZoom.current = zoom;
    const anchor = zoomAnchor.current;
    const workspace = workspaceRef.current;
    if (workspace && fitPage.current) {
      const area = pageRefs.current.get(fitPage.current);
      const paper = area?.querySelector("svg.ms-paper");
      fitPage.current = null;
      zoomAnchor.current = null;
      if (paper) {
        const viewport = workspace.getBoundingClientRect();
        const rect = paper.getBoundingClientRect();
        workspace.scrollTop += area.getBoundingClientRect().top - viewport.top;
        workspace.scrollLeft +=
          rect.left +
          rect.width / 2 -
          viewport.left -
          workspace.clientWidth / 2;
      }
      return;
    }
    if (!anchor || !workspace) return;
    zoomAnchor.current = null;
    if (!anchor.paper.isConnected) return;
    // Measure again after layout: page gaps and centering do not scale with zoom.
    const rect = anchor.paper.getBoundingClientRect();
    workspace.scrollLeft += rect.left + anchor.x * zoom - anchor.clientX;
    workspace.scrollTop += rect.top + anchor.y * zoom - anchor.clientY;
  }, [zoom, fitRequest]);
  const content = useMemo(() => contentIndex(liveMenus), [liveMenus]);
  const headingPreview = useMemo(() => {
    if (!doc || !Object.keys(headingDrafts).length) return doc;
    return alignHeadingWinglets({ ...doc, pages: doc.pages.map((p) => ({ ...p, elements: p.elements.map((n) =>
      headingDrafts[n.id] === undefined ? n : { ...n, textOverride: headingDrafts[n.id] }) })) }, content);
  }, [doc, headingDrafts, content]);
  const page = doc?.pages.find((p) => p.id === pageId) || doc?.pages[0];
  const hasDocument = Boolean(doc);
  useLayoutEffect(() => {
    const workspace = workspaceRef.current;
    const root = dialogRef.current;
    if (!workspace || !root) return;
    const alignChrome = () => {
      const center =
        workspace.getBoundingClientRect().left -
        root.getBoundingClientRect().left +
        workspace.clientWidth / 2;
      root.style.setProperty("--ms-canvas-center", `${center}px`);
    };
    alignChrome();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(alignChrome)
        : null;
    observer?.observe(workspace);
    window.addEventListener("resize", alignChrome);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", alignChrome);
    };
  }, [hasDocument, libraryOpen, inspectorOpen, preview, view]);
  // Explicit navigation scrolls; scrolling itself only changes the editing context.
  const setPageId = (id) => {
    pendingPage.current = id;
    setActivePageId(id);
    const target = pageRefs.current.get(id || doc?.pages[0].id);
    if (target && workspaceRef.current) {
      const workspace = workspaceRef.current;
      workspace.scrollTop +=
        target.getBoundingClientRect().top -
        workspace.getBoundingClientRect().top -
        24;
      pendingPage.current = undefined;
    }
  };
  useEffect(() => {
    if (pendingPage.current === undefined || !doc) return;
    const target = pageRefs.current.get(pendingPage.current || doc.pages[0].id);
    if (!target) return;
    const workspace = workspaceRef.current;
    workspace.scrollTop +=
      target.getBoundingClientRect().top -
      workspace.getBoundingClientRect().top -
      24;
    pendingPage.current = undefined;
  });
  const activatePage = (target) => {
    if (target.id === page.id) return;
    setActivePageId(target.id);
    if (!activeStyleGroup && scope === "selection") setSelected([]);
    setGuides([]);
    setMarquee(null);
  };
  const trackVisiblePage = () => {
    if (
      (gesture.current && gesture.current.type !== "pan") ||
      pendingPage.current !== undefined ||
      dialog
    )
      return;
    const workspace = workspaceRef.current.getBoundingClientRect();
    const focusY = workspace.top + workspace.height / 2;
    let closest = page,
      distance = Infinity;
    doc.pages.forEach((candidate) => {
      const rect = pageRefs.current.get(candidate.id)?.getBoundingClientRect();
      if (!rect) return;
      const nextDistance = Math.max(rect.top - focusY, focusY - rect.bottom, 0);
      if (nextDistance < distance) {
        closest = candidate;
        distance = nextDistance;
      }
    });
    activatePage(closest);
  };
  const issues = useMemo(
    () => (doc ? preflight(doc, content) : []),
    [doc, content]
  );
  const nodes = useMemo(
    () => (scope === "selection" ? page?.elements : doc?.pages.flatMap((p) => p.elements))?.filter((n) => selected.includes(n.id)) || [],
    [doc, page, selected, scope]
  );
  // Menu structure: a selected dish in a flowing section, its container and
  // the spacing handles between that container's children.
  const flowSelection = useMemo(() => (doc?.blocks && page ? flowChildFor(doc, page, selected) : null), [doc, page, selected]);
  const pinnedSelection = useMemo(() => (doc?.blocks && page && !flowSelection ? flowChildFor(doc, page, selected, { pinnedOnly: true }) : null), [doc, page, selected, flowSelection]);
  const activeFlow = flowSelection?.containerId || (focusedLayerId && doc?.blocks?.[focusedLayerId]?.layout && doc.blocks[focusedLayerId].layout.mode !== "free" ? focusedLayerId : null);
  const gaps = useMemo(() => (activeFlow && page ? gapHandles(doc, page, activeFlow) : []), [doc, page, activeFlow]);
  const pinFlow = (pin) => {
    const target = pin ? flowSelection : pinnedSelection;
    if (!target) return;
    commitAuthored((d) => pinChild(d, doc, page.id, target.containerId, target.child, pin));
  };
  const notice = useCallback((message) => setToast(message), []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  useLayoutEffect(() => {
    const root = dialogRef.current,
      previousOverflow = document.body.style.overflow,
      hadStudioClass =
        document.documentElement.classList.contains("menu-studio-open");
    document.documentElement.classList.add("menu-studio-open");
    if (!root.open) root.showModal();
    root.focus({ preventScroll: true });
    document.body.style.overflow = "hidden";
    return () => {
      root.close();
      document.body.style.overflow = previousOverflow;
      if (!hadStudioClass)
        document.documentElement.classList.remove("menu-studio-open");
    };
  }, []);
  const fit = useCallback(() => {
    const workspace = workspaceRef.current;
    const area = pageRefs.current.get(page?.id);
    const paper = area?.querySelector("svg.ms-paper");
    if (
      !workspace ||
      !page ||
      !paper ||
      !workspace.clientWidth ||
      !workspace.clientHeight
    )
      return;
    // Labels, rulers, caption, and page gaps remain fixed while the paper scales.
    const chromeHeight = Math.max(
      136,
      area.getBoundingClientRect().height - paper.getBoundingClientRect().height
    );
    const nextZoom = Math.min(
      1.3,
      Math.max(
        0.15,
        Math.min(
          (workspace.clientWidth - 112) / page.width,
          (workspace.clientHeight - chromeHeight) / page.height
        )
      )
    );
    zoomAnchor.current = null;
    fitPage.current = page.id;
    setZoomValue(nextZoom);
    setFitRequest((request) => request + 1);
    return true;
  }, [page]);
  useEffect(() => {
    if (!page || zoomFitDone.current) return;
    const frame = requestAnimationFrame(() => {
      if (fit()) zoomFitDone.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, [page, fit]);
  const updatePage = (changes) =>
    commit((d) => ({
      ...d,
      pages: d.pages.map((p) => (p.id === page.id ? { ...p, ...changes } : p)),
    }));
  const patch = useCallback(
    (changes, includeLocked = false, ids = selected) =>
      commit((d) => {
        const allowed = d.pages
          .flatMap((p) => p.elements)
          .filter((n) => ids.includes(n.id) && (includeLocked || !n.locked))
          .map((n) => n.id);
        // Full replacement supports removing a print override, which a shallow merge cannot do.
        if (typeof changes === "function")
          return {
            ...d,
            pages: d.pages.map((p) => ({
              ...p,
              elements: p.elements.map((n) =>
                allowed.includes(n.id) ? changes(n) : n
              ),
            })),
          };
        return patchElements(d, allowed, changes);
      }),
    [commit, selected]
  );
  const stylePatch = (changes) => {
    if (activeStyleGroup) {
      commit((d) => fitPriceFrames(applyStyleGroup(d, activeStyleGroup, changes), content));
      return;
    }
    if (nodes.length)
      commit((d) => {
        const anchorPage = d.pages.find((p) => p.elements.some((n) => n.id === selection[0])) || page;
        const anchor = anchorPage.elements.find((n) => n.id === selection[0]) || nodes[0];
        return fitPriceFrames(applyTypography(d, anchor, scope, anchorPage.id, selected, changes), content);
      });
  };

  const add = (type, role, text, extra = {}) => {
    const n = element(
      role,
      text,
      page.margin + 12,
      Math.min(page.height - 120, 150),
      type === "line" ? 220 : type === "text" ? 240 : 120,
      type === "line" ? 2 : type === "text" ? 64 : 100,
      { type, ...extra }
    );
    updatePage({ elements: [...page.elements, n] });
    setSelected([n.id]);
    setTool("select");
    setScope("selection");
  };
  const remove = useCallback(() => {
    if (!page) return;
    commit((d) => ({
      ...d,
      pages: d.pages.map((p) =>
        p.id === page.id
          ? {
              ...p,
              elements: p.elements.filter(
                (n) => !selected.includes(n.id) || n.locked
              ),
            }
          : p
      ),
    }));
    setSelected([]);
  }, [commit, page, selected, setSelected]);
  const paste = useCallback(
    (source) => {
      if (!page || !source.length) return;
      const sourceIds = new Set(source.map((n) => n.id));
      source = [...source, ...doc.pages.flatMap((p) => p.elements).filter((n) => n.headingWinglet && sourceIds.has(n.attachedTo) && !sourceIds.has(n.id))];
      const groups = {};
      const copiedIds = new Map(source.map((n) => [n.id, uid()]));
      const copied = clone(source).map((n) => ({
        ...(n.role === "price" || n.binding?.endsWith(":price") ? detachContent(n, content) : n),
        id: copiedIds.get(n.id),
        attachedTo: copiedIds.get(n.attachedTo),
        headingWinglet: copiedIds.has(n.attachedTo) ? n.headingWinglet : undefined,
        inlineRow: n.inlineRow && copiedIds.has(n.inlineRow.id) && page.elements.filter((other) => other.inlineRow?.id === n.inlineRow.id).every((other) => copiedIds.has(other.id)) ? { ...n.inlineRow, id: copiedIds.get(n.inlineRow.id) } : undefined,
        x: n.x + 16,
        y: n.y + 16,
        locked: false,
        ...withGroupPath({}, groupPath(n).map((g) => ({ ...g, id: (groups[g.id] ||= uid()) }))),
      }));
      commit((d) => ({
        ...d,
        pages: d.pages.map((p) =>
          p.id === page.id ? { ...p, elements: [...p.elements, ...copied] } : p
        ),
      }));
      setSelected(copied.map((n) => n.id));
    },
    [commit, page, setSelected, content, doc]
  );
  const duplicate = useCallback(() => paste(portableSelection(doc.pages.flatMap((p) => p.elements), nodes.map((n) => n.id), focusedLayerId)), [paste, nodes, doc, focusedLayerId]);
  const group = useCallback(() => {
    if (!page) return;
    commit((d) => ({ ...d, pages: d.pages.map((p) => p.id === page.id ? createGroup(p, selected) : p) }));
  }, [page, selected, commit]);
  const ungroup = useCallback(() => {
    if (!page) return;
    commit((d) => ({ ...d, pages: d.pages.map((p) => p.id === page.id ? ungroupSelection(p, selected, focusedLayerId) : p) }));
  }, [page, selected, commit, focusedLayerId]);
  const reorder = (delta) => updatePage({ elements: reorderLayers(page, selected, delta, focusedLayerId).elements });
  const moveLayer = (ids, target, placement) => {
    const next = moveLayers(page, Array.isArray(ids) ? ids : [ids], target, placement);
    if (next !== page) updatePage({ elements: next.elements });
  };
  const moveToPage = (target) => {
    const ids = nodes.filter((n) => !n.locked).map((n) => n.id);
    if (!ids.length) return;
    commit((d) => transferToPage(d, ids, target));
    setPageId(target);
    setSelected(ids);
  };
  const startEdit = (node) => {
    if (node.type !== "text" || node.locked || preview || editing?.id === node.id) return;
    if (editing) inlineRefs.current.get(editing.id)?.save();
    setTextEditors((previous) => previous.some((n) => n.id === node.id) ? previous : [...previous, node]);
    setSelected([node.id]);
    setEditing(node);

  };
  const applyCmsResults = (changes, results) => {
    results.forEach((updated, i) => {
      if (!updated || !changes[i]) return;
      liveMenusRef.current = replaceCmsSource(liveMenusRef.current, changes[i].type, updated);
      onCmsUpdate?.(changes[i].type, updated);
    });
    setLiveMenus(liveMenusRef.current);
  };
  onCmsResults.current = applyCmsResults;
  const saveText = (node, text) => {
    // Keep editors responsive while serializing writes and their undo effects.
    const task = textSaveQueue.current.then(() => performTextSave(node, text));
    textSaveQueue.current = task.catch(() => {});
    return task;
  };
  // Linked text saves the CMS field and the design change together: the API
  // commits both or neither, and undo reverses both.
  const performTextSave = async (node, text) => {
    let change = null, prospective = liveMenusRef.current;
    if (node.binding) {
      const candidate = cmsTextChange(liveMenusRef.current, node.binding, text);
      if (JSON.stringify(candidate.changes) !== JSON.stringify(candidate.expected)) {
        change = candidate;
        const sections = Object.values(liveMenusRef.current || {}).flatMap((m) => m?.sections || []);
        const source = change.type === "section" ? sections.find((x) => x._id === change.id) : sections.flatMap((x) => x.items || []).find((x) => x._id === change.id);
        prospective = replaceCmsSource(liveMenusRef.current, change.type, { ...source, ...change.changes });
      }
    }
    const nextContent = contentIndex(prospective);
    const edit = (d) => fitDescriptionFrames(fitPriceFrames({ ...d, pages: d.pages.map((p) => ({ ...p, elements: p.elements.map((n) => {
      if (n.id !== node.id) return n;
      const next = { ...n, text, name: text.slice(0, 60) };
      delete next.textOverride;
      return next;
    }) })) }, nextContent), nextContent, [node.id]);
    if (!change) { commit(edit); return; }
    const results = await commitWithCms(edit, [change]);
    applyCmsResults([change], results);
  };
  const point = (e) => {
    const rect = (
      e.target.closest?.("svg.ms-paper") || svgRef.current
    ).getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom,
    };
  };
  const beginGesture = (e, data) => {
    e.preventDefault();
    e.stopPropagation();
    gesture.current = {
      ...data,
      start: point(e),
      clientX: e.clientX,
      clientY: e.clientY,
      base: current.current,
      sourcePageId: e.target.closest?.("[data-page-id]")?.dataset.pageId || page.id,
    };
  };
  const backgroundDown = (e, targetPage = page) => {
    activatePage(targetPage);
    if (e.button !== 0 && e.button !== 1) return;
    if (tool === "hand" || space.current || e.button === 1) {
      beginGesture(e, {
        type: "pan",
        left: workspaceRef.current.scrollLeft,
        top: workspaceRef.current.scrollTop,
      });
      return;
    }
    if (preview) return;
    if (!e.shiftKey) setSelected([]);
    beginGesture(e, {
      type: "marquee",
      initial: e.shiftKey && targetPage.id === page.id ? selected : [],
    });
  };
  const nodeDown = (e, node, targetPage = page) => {
    activatePage(targetPage);
    const pageSelection =
      !activeStyleGroup && targetPage.id === page.id ? selected : [];
    if (
      activeStyleGroup &&
      e.shiftKey &&
      e.button === 0 &&
      tool !== "hand" &&
      !space.current
    ) {
      e.preventDefault();
      e.stopPropagation();
      commit((d) => toggleStyleGroupMember(d, activeStyleGroup, node.id));
      dialogRef.current?.focus({ preventScroll: true });
      return;
    }
    if (e.button !== 0 && e.button !== 1) return;
    if (tool === "hand" || space.current || e.button === 1) {
      backgroundDown(e, targetPage);
      return;
    }
    e.stopPropagation();
    if (preview) return;
    const direct = e.metaKey || e.ctrlKey;
    const selection = canvasGroupSelection(targetPage, node, pageSelection, direct, focusedLayerId);
    let ids = selection.ids;
    if (e.shiftKey) {
      ids = canvasShiftSelection(targetPage, node, pageSelection, direct, focusedLayerId);
    } else if (!direct && pageSelection.includes(node.id)) {
      ids = pageSelection;
    }
    const drillInto = !direct && !e.shiftKey ? selection.drill : null;
    setSelected(ids, e.shiftKey ? null : selection.focusId);
    dialogRef.current?.focus({ preventScroll: true });
    if (node.locked || !ids.length) return;
    beginGesture(e, {
      type: "move",
      drillInto,
      drillFocusId: selection.drillFocusId,
      ids: ids.filter(
        (id) => !targetPage.elements.find((n) => n.id === id)?.locked
      ),
    });
  };
  const resizeDown = (e, node, corner) =>
    beginGesture(e, {
      type: "resize",
      node,
      corner,
      ids: selected.filter(
        (id) => {
          const target = doc.pages.flatMap((p) => p.elements).find((n) => n.id === id);
          return target && !target.locked && target.type === node.type;
        }
      ),
    });
  const gapDown = (e, gap) => beginGesture(e, { type: "gap", containerId: activeFlow, key: gap.key, startGap: gap.gap, all: e.shiftKey });
  const rotateDown = (e, node) => {
    const p = point(e);
    beginGesture(e, {
      type: "rotate",
      node,
      angle: Math.atan2(p.y - node.y - node.h / 2, p.x - node.x - node.w / 2),
    });
  };
  useStudioGestures({ gesture, page, zoom, grid, snap, previewDoc, commit, commitAuthored, current, setSelected,
    workspaceRef, pageRefs, setMarquee, setDragVisual, setGuides, setActivePageId });
  useStudioShortcuts({ view, preview, setPreview, doc, focusedLayerId, dialog, activeStyleGroup, setSelected, page, nodes, duplicate, group, historyBusy,
    ungroup, paste, patch, redo, remove, save, undo, notice, space, clipboard, setTool });
  useEffect(() => {
    const el = workspaceRef.current;
    if (!el) return;
    const wheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setZoom(
          (z) => Math.min(3, Math.max(0.15, z * Math.exp(-e.deltaY * 0.008))),
          e
        );
      }
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, [doc, setZoom, view, preview]);
  const upload = async (event, replacing = false) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    const ids = replacing ? [...replacementIds.current] : [];
    if (
      !["image/png", "image/jpeg", "image/webp"].includes(file.type) ||
      file.size > 2 * 1024 * 1024
    ) {
      notice("Choose a PNG, JPEG or WebP image under 2 MB.");
      return;
    }
    try {
      if (replacing) setReplacingArtwork(true);
      const src = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = src;
      });
      if (replacing) {
        const targets = doc.pages.flatMap((p) => p.elements).filter((n) => ids.includes(n.id) && n.type === "image" && !n.locked);
        let originals = Object.values(initialDocuments || {}).flatMap((d) => d.pages.flatMap((p) => p.elements));
        const inheritedTargets = targets.filter((n) => !n.headingWinglet);
        if (inheritedTargets.some((n) => !n.artworkTransform && n.name.startsWith("Original ")) && importOriginal) {
          const seeds = await importOriginal();
          originals = Object.values(seeds).flatMap((d) => d.pages.flatMap((p) => p.elements));
        }
        const orientations = await recoverArtworkTransforms(inheritedTargets, originals);
        const cache = new Map(), replacements = {}, source = uid("artwork");
        for (const n of targets) {
          const matrix = replacementArtworkMatrix(n, orientations.get(n.src)), key = matrix.join(",");
          if (!cache.has(key)) {
            const maxPixels = Math.ceil(Math.max(...targets.filter((target) =>
              replacementArtworkMatrix(target, orientations.get(target.src)).join(",") === key
            ).map((target) => Math.max(target.w, target.h))) * 300 / 72);
            cache.set(key, transformArtwork(img, matrix, maxPixels));
          }
          replacements[n.id] = { ...cache.get(key), artworkSource: source, artworkTransform: matrix };
        }
        commit((d) => replaceArtwork(d, replacements));
        notice(`Replaced ${targets.length} artwork${targets.length === 1 ? "" : "s"}. Undo restores the previous artwork.`);
        return;
      }
      const w = Math.min(240, page.width - 2 * page.margin);
      add("image", "decoration", "", {
        src,
        name: file.name,
        w,
        h: (w * img.naturalHeight) / img.naturalWidth,
        sourceWidth: img.naturalWidth,
        sourceHeight: img.naturalHeight,
      });
    } catch (err) {
      notice(err.message || "This image could not be read.");
    } finally {
      if (replacing) setReplacingArtwork(false);
    }
  };
  const importDesign = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    try {
      if (file.size > 6 * 1024 * 1024)
        throw new Error("Design files must be under 6 MB.");
      const next = unpackArtwork(JSON.parse(await file.text()));
      if (!validateDocument(next))
        throw new Error("This is not a valid Menu Studio design file.");
      commitAuthored({ ...next, menuKey, originalImportCompleted: true });
      setPageId(next.pages[0].id);
      setSelected([]);
      notice("Design imported. Undo restores the previous design.");
    } catch (err) {
      notice(err.message || "Unable to import design.");
    }
  };
  const close = async () => {
    if (!(await saveInlineEdits())) return;
    await save();
    onClose();
  };
  const switchMenu = async (key) => {
    if (key !== menuKey) {
      if (!(await saveInlineEdits())) return;
      await save();
      setMenuKey(key);
    }
    setView("design");
    onContextChange?.(key === "takeout" ? "takeout" : "full");
  };
  const showCms = async () => {
    if (view === "cms" || !(await saveInlineEdits())) return;
    await save();
    setView("cms");
    onContextChange?.("cms");
  };
  return createPortal(
    <dialog
      ref={dialogRef}
      className={`menu-studio${view === "cms" ? " is-cms-view" : ""}`}
      tabIndex={-1}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      aria-label="Trattoria Demi Menu Studio"
      aria-busy={historyBusy}
      onPointerDownCapture={(e) => { if (historyBusy) { e.preventDefault(); e.stopPropagation(); } }}
      onClickCapture={(e) => { if (historyBusy) { e.preventDefault(); e.stopPropagation(); } }}
      onCancel={(e) => {
        e.preventDefault();
        setSelected([]);
      }}
    >
      <header className="ms-topbar">
        <div className="ms-brand">
          <Button icon="back" title="Close Menu Studio" onClick={close} />
          <div>
            <strong>Menu Studio</strong>
          </div>
        </div>
        <div className="ms-document-title">
          <span>{view === "cms" ? "Menu CMS" : doc?.name || "Opening your menu…"}</span>
          <button onClick={save} title="Save design" className="ms-save-status">
            <span className={`ms-dot ${error ? "is-warning" : ""}`} />
            {status}
          </button>
        </div>
        <div className="ms-top-actions">
          <Button
            icon="save"
            title="Save design (⌘S)"
            onClick={save}
            disabled={!doc}
          />
          <Button
            icon="eye"
            active={preview}
            onClick={() => {
              historyAction(() => { setPreview(!preview); setSelected([]); });
            }}
          >
            Preview
          </Button>
          <Button
            icon="export"
            className="ms-header-export"
            onClick={openExport}
            disabled={!doc}
          >
            Export menu
          </Button>
        </div>
      </header>
      <div className="ms-toolbar">
        <nav className="ms-context-switch" aria-label="Menu Studio views">
          <button type="button" className={view === "design" && menuKey === "dinner" ? "is-active" : ""} aria-current={view === "design" && menuKey === "dinner" ? "page" : undefined} onClick={() => switchMenu("dinner")} disabled={!doc}>Full menu</button>
          <button type="button" className={view === "design" && menuKey === "takeout" ? "is-active" : ""} aria-current={view === "design" && menuKey === "takeout" ? "page" : undefined} onClick={() => switchMenu("takeout")} disabled={!doc}>Takeout menu</button>
          <button type="button" className={view === "cms" ? "is-active" : ""} aria-current={view === "cms" ? "page" : undefined} onClick={showCms}>CMS</button>
        </nav>
        {view === "design" && doc && !preview && <>
          <div className="ms-button-row ms-canvas-tools">
            <Button icon="arrow" title="Selection tool (V)" active={tool === "select"} onClick={() => setTool("select")} />
            <Button icon="hand" title="Hand tool (H)" active={tool === "hand"} onClick={() => setTool("hand")} />
            <span className="ms-toolbar-divider" />
            <Button icon="undo" title="Undo (⌘Z)" onClick={undo} disabled={!canUndo} />
            <Button icon="redo" title="Redo (⌘⇧Z)" onClick={redo} disabled={!canRedo} />
            <span className="ms-toolbar-divider" />
            <Button icon="duplicate" title="Duplicate selection (⌘D)" onClick={duplicate} disabled={!!activeStyleGroup || !nodes.length} />
            <Button icon="trash" title="Delete selection" onClick={remove} disabled={!!activeStyleGroup || !nodes.some((n) => !n.locked)} />
          </div>
          <div className="ms-button-row ms-toolbar-help">
            <Button icon="help" title="Keyboard shortcuts" onClick={() => setDialog("help")} />
          </div>
        </>}
      </div>
      {cmsView && <div className="ms-cms-workspace" style={{ display: view === "cms" ? "block" : "none" }}>{cmsView}</div>}
      {view !== "cms" && (!doc ? (
        <div className="ms-loading" role="status" aria-live="polite">
          <span className="ms-loading-indicator" aria-hidden="true" />
          <h2>Opening Menu Studio</h2>
          <p>Loading your menu…</p>
        </div>
      ) : (
        <>
          {error && (
            <div className="ms-cloud-error" role="alert">
              <Icon name="warning" size={16} />
              <span>{error}</span>
              <Button onClick={save}>Retry Save</Button>
              {/newer|conflict|changed/.test(error) && (
                <Button onClick={useCloud}>Use cloud version</Button>
              )}
              <Button onClick={openExport}>Export PDF</Button>
              <Button
                icon="close"
                title="Dismiss save notice"
                onClick={() => setError("")}
              />
            </div>
          )}
          <div className={`ms-body ${preview ? "is-preview" : ""}`}>
            {preview ? <StudioMenuPreview doc={doc} content={content} /> : <>
            <nav className="ms-tool-rail" aria-label="Design tools">
              {[
                ["pages", "Pages"],
                ["layers", "Layers"],
                ["text", "Text"],
                ["shapes", "Elements"],
                ["image", "Artwork"],
                ["styles", "Styles"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  className={tab === key && libraryOpen ? "is-active" : ""}
                  onClick={() => {
                    setTab(key);
                    setLibraryOpen(true);
                    if (window.innerWidth <= 700) setInspectorOpen(false);
                    setPreview(false);
                  }}
                >
                  <Icon name={key} size={18} />
                  <span>{label}</span>
                </button>
              ))}
              <span className="ms-rail-spacer" />
            </nav>
            {!preview && libraryOpen && (
              <StudioLibrary
                importOriginal={async () => {
                  try {
                    const original = await importOriginal();
                    commitAuthored(original[menuKey]);
                    setPageId(null);
                    setSelected([]);
                    notice(
                      "Original PDF restored. Undo returns to your previous design."
                    );
                  } catch (err) {
                    notice(err.message);
                  }
                }}
                activeStyleGroup={activeStyleGroup}
                selectStyleGroup={(role) => {
                  setActiveStyleGroup(role);
                  setScope("selection");
                  setInspectorOpen(true);
                  if (window.innerWidth <= 700) setLibraryOpen(false);
                  const ids = styleGroupIds(doc, role);
                  if (!page.elements.some((n) => ids.includes(n.id))) {
                    const first = doc.pages.find((p) =>
                      p.elements.some((n) => ids.includes(n.id))
                    );
                    if (first) setPageId(first.id);
                  }
                }}
                tab={tab}
                doc={doc}
                page={page}
                content={content}
                selected={selected}
                setSelected={setSelected}
                setPageId={setPageId}
                commit={commit}
                add={add}
                uploadRef={uploadRef}
                importRef={importRef}
                applyTemplate={(columns) => {
                  setDialog({ type: "template", columns });
                }}
                patch={patch}
                focusedLayerId={focusedLayerId}
                moveLayer={moveLayer}
                group={group}
                ungroup={ungroup}
                setScope={setScope}
                maintenance={{ authored, commitAuthored, repairs, applyRepair, unresolved, revision: getRevision, recoveryState }}
              />
            )}
            {!preview && (
              <div
                className={`ms-panel-edge ms-library-edge ${
                  libraryOpen ? "is-open" : ""
                }`}
              >
                <Button
                  className="ms-panel-toggle"
                  title="Toggle library"
                  aria-expanded={libraryOpen}
                  aria-controls="ms-library-panel"
                  onClick={() => {
                    setLibraryOpen(!libraryOpen);
                    if (window.innerWidth <= 700) setInspectorOpen(false);
                  }}
                >
                  <span aria-hidden="true">{libraryOpen ? "<<" : ">>"}</span>
                </Button>
              </div>
            )}
            <main
              className={`ms-workspace ${tool === "hand" ? "is-hand" : ""}`}
              ref={workspaceRef}
              onScroll={trackVisiblePage}
              onPointerDown={(e) => {
                // Page wrappers and captions also cover the black workspace.
                // Only the paper itself owns selection and marquee gestures.
                if (e.target.closest("svg.ms-paper")) return;
                if (tool === "hand" || space.current || e.button === 1)
                  backgroundDown(e);
                else if (e.button === 0) {
                  setSelected([]);
                  dialogRef.current?.focus({ preventScroll: true });
                }
              }}
            >
              {doc.pages.map((canvasPage) => (
                <div
                  key={canvasPage.id}
                  ref={(el) => {
                    if (el) pageRefs.current.set(canvasPage.id, el);
                    else pageRefs.current.delete(canvasPage.id);
                  }}
                  data-page-id={canvasPage.id}
                  className="ms-canvas-area"
                  style={{
                    minWidth: canvasPage.width * zoom + 112,
                    minHeight: canvasPage.height * zoom + 112,
                  }}
                >
                  <div
                    className="ms-canvas-label"
                    style={{ width: canvasPage.width * zoom }}
                  >
                    <span className="ms-canvas-page-name">
                      <span>
                        {String(doc.pages.indexOf(canvasPage) + 1).padStart(
                          2,
                          "0"
                        )}
                      </span>
                      <StudioPageName
                        page={canvasPage}
                        number={doc.pages.indexOf(canvasPage) + 1}
                        location="canvas"
                        onRename={(name) =>
                          commit((d) => ({
                            ...d,
                            pages: d.pages.map((candidate) =>
                              candidate.id === canvasPage.id
                                ? { ...candidate, name }
                                : candidate
                            ),
                          }))
                        }
                      />
                    </span>
                    <span>
                      {(canvasPage.width / 72).toFixed(2)} ×{" "}
                      {(canvasPage.height / 72).toFixed(2)} in
                    </span>
                  </div>
                  <div className="ms-paper-wrap">
                    <div
                      className="ms-ruler-x"
                      style={{ width: canvasPage.width * zoom }}
                    >
                      {Array.from(
                        { length: Math.floor(canvasPage.width / 72) + 1 },
                        (_, i) => (
                          <span key={i} style={{ left: i * 72 * zoom }}>
                            {i}
                          </span>
                        )
                      )}
                    </div>
                    <div
                      className="ms-ruler-y"
                      style={{ height: canvasPage.height * zoom }}
                    >
                      {Array.from(
                        { length: Math.floor(canvasPage.height / 72) + 1 },
                        (_, i) => (
                          <span key={i} style={{ top: i * 72 * zoom }}>
                            {i}
                          </span>
                        )
                      )}
                    </div>
                    <StudioCanvas
                      doc={doc}
                      page={headingPreview.pages.find((p) => p.id === canvasPage.id)}
                      content={content}
                      focusedLayerId={focusedLayerId}
                      dragHiddenIds={dragVisual?.nodes.map((n) => n.id)}
                      selected={
                        dragVisual || preview ||
                        (!activeStyleGroup && scope === "selection" && canvasPage.id !== page.id)
                          ? []
                          : selected
                      }
                      zoom={zoom}
                      grid={grid}
                      margins={margins}
                      guides={canvasPage.id === page.id ? guides : []}
                      onNodeDown={(e, node) => nodeDown(e, node, canvasPage)}
                      onBackgroundDown={(e) => backgroundDown(e, canvasPage)}
                      onEdit={startEdit}
                      editingId={editing?.id}
                      editingIds={textEditors.map((editor) => editor.id)}
                      onResize={resizeDown}
                      onRotate={rotateDown}
                      svgRef={canvasPage.id === page.id ? svgRef : undefined}
                      preview={preview}
                      marquee={canvasPage.id === page.id ? marquee : null}
                      selectionOnly={!!activeStyleGroup || scope !== "selection"}
                      gaps={canvasPage.id === page.id && !dragVisual ? gaps : []}
                      onGapDown={gapDown}
                    />
                    {textEditors.filter((editor) => canvasPage.elements.some((n) => n.id === editor.id)).map((editor) => (
                      <StudioInlineText ref={(value) => { if (value) inlineRefs.current.set(editor.id, value); else inlineRefs.current.delete(editor.id); }} key={editor.id} node={canvasPage.elements.find((n) => n.id === editor.id)} doc={doc} content={content}
                        active={editing?.id === editor.id}
                        onDraftChange={editor.role === "section" ? (text) => setHeadingDrafts((previous) => ({ ...previous, [editor.id]: text })) : undefined}
                        zoom={zoom} onSave={saveText} onResize={resizeDown}
                        onClose={() => closeTextEditor(editor.id)} />
                    ))}
                  </div>
                  <p className="ms-canvas-caption">
                    {preview
                      ? "PRINT PREVIEW · Guides and selection hidden"
                      : canvasPage.id === page.id && selected.length
                      ? "DRAG TO POSITION · SHIFT FOR CONSTRAINTS · ALT TO IGNORE SNAPPING"
                      : "CRAFTED FOR THE TABLE"}
                  </p>
                </div>
              ))}
            </main>
            {dragVisual && <svg className="ms-drag-overlay" aria-hidden="true" data-testid="drag-preview"
              width={dragVisual.width} height={dragVisual.height}
              style={{ position: "fixed", left: dragVisual.left, top: dragVisual.top, pointerEvents: "none", zIndex: 20, overflow: "hidden" }}>
              <g transform={`translate(${dragVisual.x} ${dragVisual.y}) scale(${zoom})`}>
                {dragVisual.nodes.map((node) => <g key={node.id} transform={`translate(${node.x} ${node.y}) rotate(${node.rotation || 0} ${node.w / 2} ${node.h / 2})`}>
                  <NodeArtwork node={node} doc={doc} content={content} />
                </g>)}
                <rect x={dragVisual.box.x} y={dragVisual.box.y} width={dragVisual.box.w} height={dragVisual.box.h}
                  fill="none" stroke="#b88435" strokeWidth={1 / zoom} />
              </g>
            </svg>}
            {!preview && (
              <div
                className={`ms-panel-edge ms-inspector-edge ${
                  inspectorOpen ? "is-open" : ""
                }`}
              >
                <Button
                  className="ms-panel-toggle"
                  title="Toggle inspector"
                  aria-expanded={inspectorOpen}
                  aria-controls="ms-inspector-panel"
                  onClick={() => {
                    setInspectorOpen(!inspectorOpen);
                    if (window.innerWidth <= 700) setLibraryOpen(false);
                  }}
                >
                  <span aria-hidden="true">{inspectorOpen ? ">>" : "<<"}</span>
                </Button>
              </div>
            )}
            {!preview && inspectorOpen && (
              <StudioInspector
                replacingArtwork={replacingArtwork}
                onReplaceArtwork={() => {
                  replacementIds.current = [...selected];
                  replaceArtworkRef.current.click();
                }}
                onEdit={startEdit}
                styleGroup={
                  activeStyleGroup
                    ? {
                        name: styleGroupName(doc, activeStyleGroup),
                        role: activeStyleGroup,
                      }
                    : null
                }
                deleteStyleGroup={() => {
                  commit((d) => deleteStyleGroup(d, activeStyleGroup));
                  setActiveStyleGroup(null);
                  setSelection([]);
                }}
                renameStyleGroup={(name) =>
                  commit((d) => updateStyleGroup(d, activeStyleGroup, { name }))
                }
                doc={doc}
                menus={liveMenus}
                page={page}
                selected={selected}
                content={content}
                scope={scope}
                setScope={setScope}
                patch={patch}
                stylePatch={stylePatch}
                updatePage={updatePage}
                commit={commit}
                align={(direction) =>
                  commit((d) => alignElements(d, page.id, selected, direction))
                }
                distribute={(axis) =>
                  commit((d) => distributeElements(d, page.id, selected, axis, focusedLayerId))
                }
                duplicate={duplicate}
                remove={remove}
                reorder={reorder}
                group={group}
                ungroup={ungroup}
                moveToPage={moveToPage}
                commitNodes={(changed) => {
                  if (changed.size) commit((d) => ({ ...d, pages: d.pages.map((p) => p.elements.some((n) => changed.has(n.id)) ? { ...p, elements: p.elements.map((n) => changed.get(n.id) || n) } : p) }));
                }}
                select={setSelected}
                flow={flowSelection ? { pinned: false, onToggle: () => pinFlow(true) } : pinnedSelection ? { pinned: true, onToggle: () => pinFlow(false) } : null}
              />
            )}
            </>}
          </div>
          <footer className="ms-statusbar" style={preview ? { display: "none" } : undefined}>
            <div className="ms-button-row">
              <span className="ms-page-count">
                Page {doc.pages.indexOf(page) + 1} of {doc.pages.length}
              </span>
              <Button
                icon="back"
                title="Previous page"
                disabled={doc.pages.indexOf(page) === 0}
                onClick={() => {
                  setPageId(doc.pages[doc.pages.indexOf(page) - 1].id);
                  setSelected([]);
                }}
              />
              <Button
                icon="chevron"
                title="Next page"
                disabled={doc.pages.indexOf(page) === doc.pages.length - 1}
                onClick={() => {
                  setPageId(doc.pages[doc.pages.indexOf(page) + 1].id);
                  setSelected([]);
                }}
              />
            </div>
            <div className="ms-view-options">
              <label>
                <input
                  type="checkbox"
                  checked={snap}
                  onChange={(e) => setSnap(e.target.checked)}
                />
                Snap
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={margins}
                  onChange={(e) => setMargins(e.target.checked)}
                />
                Margins
              </label>
              <Button
                icon="grid"
                title="Toggle grid"
                active={grid}
                onClick={() => setGrid(!grid)}
              />
            </div>
            <div className="ms-button-row">
              <Button
                icon="minus"
                title="Zoom out"
                onClick={() => setZoom((z) => Math.max(0.15, z - 0.1))}
              />
              <select
                aria-label="Canvas zoom"
                value={Math.round(zoom * 100)}
                onChange={(e) => setZoom(+e.target.value / 100)}
              >
                {Array.from(
                  new Set([
                    25,
                    50,
                    75,
                    100,
                    125,
                    150,
                    200,
                    300,
                    Math.round(zoom * 100),
                  ])
                )
                  .sort((a, b) => a - b)
                  .map((z) => (
                    <option key={z} value={z}>
                      {z}%
                    </option>
                  ))}
              </select>
              <Button
                icon="plus"
                title="Zoom in"
                onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
              />
              <Button icon="fit" title="Fit page to workspace" onClick={fit} />
            </div>
          </footer>
        </>
      ))}
      <input
        ref={replaceArtworkRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(event) => upload(event, true)}
      />
      <input
        ref={uploadRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={upload}
      />
      <input
        ref={importRef}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={importDesign}
      />
      {toast && (
        <div className="ms-toast" role="status">
          {toast}
        </div>
      )}
      {dialog === "export" && doc && (
        <ExportDialog
          doc={doc}
          page={page}
          content={content}
          issues={issues}
          onClose={() => setDialog(null)}
          onIssue={(issue) => {
            setPageId(issue.pageId);
            setSelected([issue.nodeId]);
            setPreview(false);
            setInspectorOpen(true);
            setDialog(null);
          }}
        />
      )}
      {dialog === "help" && <ShortcutsDialog onClose={() => setDialog(null)} />}
      {dialog?.type === "template" && (
        <Modal
          title="Rebuild this menu layout?"
          onClose={() => setDialog(null)}
        >
          <div className="ms-modal-body">
            <p>
              This generates a fresh{" "}
              {dialog.columns === 2 ? "two-column" : "one-column"} design using
              the current CMS menu. Your custom layout and artwork will be
              replaced. You can undo this change.
            </p>
          </div>
          <footer>
            <Button onClick={() => setDialog(null)}>Keep current design</Button>
            <Button
              className="ms-primary"
              onClick={() => {
                commitAuthored({
                  ...generateDocument(
                    menus,
                    menuKey,
                    doc.themeKey === "original"
                      ? "trattoria"
                      : doc.themeKey || "trattoria",
                    dialog.columns
                  ),
                  originalImportCompleted: true,
                });
                setSelected([]);
                setPageId(null);
                setDialog(null);
              }}
            >
              Rebuild layout
            </Button>
          </footer>
        </Modal>
      )}
    </dialog>,
    document.body
  );
}
